import React, { useEffect, useState } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';

import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { CadernoLPMADestruicaoBordadura } from '../../models/modelsCadernoLPMA';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';
import MetodoLimpezaSelect from './components/MetodoLimpezaSelect';
import {
	formatarMetodoLimpezaParaJson,
	normalizarMetodoLimpezaParaFormulario,
} from '../../util/cadernoLPMA/metodoLimpeza';

interface DestruicaoBordaduraModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (info: CadernoLPMADestruicaoBordadura) => void;
	dadosDestruicaoBordadura?: CadernoLPMADestruicaoBordadura | null;
}

const defaultValues: CadernoLPMADestruicaoBordadura = {
	dataDestruicaoBordadura: null,
	dataFinalDestruicaoBordadura: null,
	talhaoDestruicaoBordadura: '',
	estadioFenologicoDestruicaoBordadura: '',
	metodoDestruicaoMecanico: false,
	metodoDestruicaoMecanicoGrade: false,
	metodoDestruicaoMecanicoRocadeira: false,
	metodoDestruicaoMecanicoEnxada: false,
	metodoDestruicaoMecanicoTriton: false,
	metodoDestruicaoMecanicoCapina: false,
	metodoDestruicaoQuimico: false,
	metodoDestruicaoQuimicoHerbicida: false,
	metodoDestruicaoQuimicoOutros: false,
	outrosMetodoDestruicaoQuimico: '',
	responsavelDestruicaoBordadura: '',
	observacoesDestruicaoBordadura: '',
};

const formatarDataParaJson = (valor: any) => {
	if (!valor) return null;
	const data = valor instanceof Date ? valor : new Date(valor);
	return Number.isNaN(data.getTime()) ? null : data.toISOString();
};

export default function DestruicaoBordaduraModal({ show, onHide, onSubmit, dadosDestruicaoBordadura }: DestruicaoBordaduraModalProps) {
	const {
		register,
		handleSubmit,
		control,
		reset,
		setValue,
		getValues,
		watch,
		formState: { errors },
	} = useForm<CadernoLPMADestruicaoBordadura>({
		mode: 'all',
		defaultValues,
	});

	const [quantidadeLinhasLimpeza, setQuantidadeLinhasLimpeza] = useState<number>(1);

	useEffect(() => {
		if (!show) {
			reset(defaultValues);
			setQuantidadeLinhasLimpeza(1);
		} else if (dadosDestruicaoBordadura) {
			const parseDate = (valor: any) => {
				if (!valor) return null;
				const data = new Date(valor);
				return Number.isNaN(data.getTime()) ? null : data;
			};

			// Parse do JSON de limpeza
			let limpezas: any[] = [];
			try {
				if (dadosDestruicaoBordadura.limpezaDestruicaoBordaduraJson) {
					limpezas = JSON.parse(dadosDestruicaoBordadura.limpezaDestruicaoBordaduraJson);
				}
			} catch (e) {}

			// Atualiza quantidade de linhas
			setQuantidadeLinhasLimpeza(Math.max(1, limpezas.length));

			// Prepara dados para resetar o formulário
			const dadosParaReset: any = {
				...dadosDestruicaoBordadura,
				dataDestruicaoBordadura: parseDate(dadosDestruicaoBordadura.dataDestruicaoBordadura),
				dataFinalDestruicaoBordadura: parseDate(dadosDestruicaoBordadura.dataFinalDestruicaoBordadura),
			};

			// Popular campos de limpeza
			limpezas.forEach((item: any, index: number) => {
				const metodoLimpeza = normalizarMetodoLimpezaParaFormulario(item.metodo);
				dadosParaReset[`limpezaNome${index}`] = item.nome || '';
				dadosParaReset[`limpezaDataEntrada${index}`] = parseDate(item.dataAntes);
				dadosParaReset[`limpezaDataSaida${index}`] = parseDate(item.dataDepois);
				dadosParaReset[`limpezaMetodo${index}`] = metodoLimpeza.metodo;
				dadosParaReset[`limpezaMetodoOutros${index}`] = metodoLimpeza.outros;
				dadosParaReset[`limpezaResponsavel${index}`] = item.responsavel || '';
			});

			reset(dadosParaReset);
		}
	}, [show, dadosDestruicaoBordadura, reset]);

	const adicionarLinhaLimpeza = () => setQuantidadeLinhasLimpeza((prev) => prev + 1);

	const removerLinhaLimpeza = (index: number) => {
		if (quantidadeLinhasLimpeza === 1) return;
		setQuantidadeLinhasLimpeza((prev) => prev - 1);
		limparCamposLimpeza(index);
	};

	const limparCamposLimpeza = (index: number) => {
		setValue(`limpezaNome${index}` as any, '');
		setValue(`limpezaDataEntrada${index}` as any, null);
		setValue(`limpezaDataSaida${index}` as any, null);
		setValue(`limpezaMetodo${index}` as any, '');
		setValue(`limpezaMetodoOutros${index}` as any, '');
		setValue(`limpezaResponsavel${index}` as any, '');
	};

	const handleSubmitInterno = (dados: CadernoLPMADestruicaoBordadura) => {
		const valores = getValues() as Record<string, any>;
		const dadosNormalizados: Record<string, any> = { ...dados };

		const limpezas: any[] = [];
		for (let i = 0; i < quantidadeLinhasLimpeza; i++) {
			const nome = valores[`limpezaNome${i}`];
			const dataEntrada = valores[`limpezaDataEntrada${i}`];
			const dataSaida = valores[`limpezaDataSaida${i}`];
			const metodo = formatarMetodoLimpezaParaJson(
				valores[`limpezaMetodo${i}`],
				valores[`limpezaMetodoOutros${i}`],
			);
			const responsavel = valores[`limpezaResponsavel${i}`];

			if (nome || dataEntrada || dataSaida || metodo || responsavel) {
				limpezas.push({
					nome,
					dataAntes: formatarDataParaJson(dataEntrada),
					dataDepois: formatarDataParaJson(dataSaida),
					metodo,
					responsavel,
				});
			}

			delete dadosNormalizados[`limpezaNome${i}`];
			delete dadosNormalizados[`limpezaDataEntrada${i}`];
			delete dadosNormalizados[`limpezaDataSaida${i}`];
			delete dadosNormalizados[`limpezaMetodo${i}`];
			delete dadosNormalizados[`limpezaMetodoOutros${i}`];
			delete dadosNormalizados[`limpezaResponsavel${i}`];
		}

		dadosNormalizados.limpezaDestruicaoBordaduraJson = limpezas.length ? JSON.stringify(limpezas) : null;

		onSubmit(dadosNormalizados as CadernoLPMADestruicaoBordadura);
		onHide();
	};

	return (
		<Modal show={show} onHide={onHide} title={STRINGS_CADERNOLPMA.DESTRUICAOBORDADURA_MODAL_TITLE} bodyClassName="caderno-lpma-modal-body">
			<Form onSubmit={handleSubmit(handleSubmitInterno)} autoComplete="off">

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Registro de destruição da bordadura</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom">
					<Col md={3}>
						<Form.Group>
							<Form.Label>Data inicial</Form.Label>
							<Controller
								control={control}
								name="dataDestruicaoBordadura"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data inicial"
									/>
								)}
							/>
							{errors.dataDestruicaoBordadura && <span className="error-field">{errors.dataDestruicaoBordadura.message}</span>}
						</Form.Group>
					</Col>
					<Col md={3}>
						<Form.Group>
							<Form.Label>Data final</Form.Label>
							<Controller
								control={control}
								name="dataFinalDestruicaoBordadura"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data final"
									/>
								)}
							/>
							{errors.dataFinalDestruicaoBordadura && <span className="error-field">{errors.dataFinalDestruicaoBordadura.message}</span>}
						</Form.Group>
					</Col>
					<Col md={3}>
						<Form.Group>
							<Form.Label>Quadra / Talhão</Form.Label>
							<Form.Control
								ref={register}
								name="talhaoDestruicaoBordadura"
								placeholder="Informe a quadra ou talhão"
								maxLength={150}
							/>
							{errors.talhaoDestruicaoBordadura && <span className="error-field">{errors.talhaoDestruicaoBordadura.message}</span>}
						</Form.Group>
					</Col>
					<Col md={3}>
						<Form.Group>
							<Form.Label>Estádio fenológico do ensaio</Form.Label>
							<Form.Control
								ref={register}
								name="estadioFenologicoDestruicaoBordadura"
								placeholder="Descreva o estádio fenológico"
								maxLength={150}
							/>
							{errors.estadioFenologicoDestruicaoBordadura && <span className="error-field">{errors.estadioFenologicoDestruicaoBordadura.message}</span>}
						</Form.Group>
					</Col>
				</Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Métodos de destruição</Form.Label>
						<div className="d-flex flex-column">
							<Form.Check type="checkbox" ref={register} name="metodoDestruicaoMecanico" label="Mecânico" />
							<div className="ml-3 d-flex flex-column">
								<Form.Check type="checkbox" ref={register} name="metodoDestruicaoMecanicoGrade" label="Grade" />
								<Form.Check type="checkbox" ref={register} name="metodoDestruicaoMecanicoRocadeira" label="Roçadeira" />
								<Form.Check type="checkbox" ref={register} name="metodoDestruicaoMecanicoEnxada" label="Enxada rotativa" />
								<Form.Check type="checkbox" ref={register} name="metodoDestruicaoMecanicoTriton" label="Triton" />
								<Form.Check type="checkbox" ref={register} name="metodoDestruicaoMecanicoCapina" label="Capina manual" />
							</div>
							<Form.Check type="checkbox" ref={register} name="metodoDestruicaoQuimico" label="Químico" />
							<div className="ml-3 d-flex flex-column">
								<Form.Check type="checkbox" ref={register} name="metodoDestruicaoQuimicoHerbicida" label="Herbicida" />
								<Form.Check type="checkbox" ref={register} name="metodoDestruicaoQuimicoOutros" label="Outro (especificar)" />
							</div>
						</div>
						<Form.Control
							className="mt-2"
							ref={register}
							name="outrosMetodoDestruicaoQuimico"
							placeholder="Detalhe o método quando a opção 'Outro' for selecionada"
							maxLength={200}
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Responsável</Form.Label>
						<Form.Control
							ref={register}
							name="responsavelDestruicaoBordadura"
							placeholder="Informe o responsável pela destruição da bordadura"
							maxLength={150}
						/>
						{errors.responsavelDestruicaoBordadura && <span className="error-field">{errors.responsavelDestruicaoBordadura.message}</span>}
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Maquinário / Equipamento</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom font-weight-bold">
					<Col md={4}>Nome / Código do equipamento utilizado</Col>
					<Col md={2}>Data de limpeza – Antes (entrada)</Col>
					<Col md={2}>Data de limpeza – Antes (saída)</Col>
					<Col md={2}>Método de limpeza</Col>
					<Col md={2}>Responsável</Col>
				</Row>

				{Array.from({ length: quantidadeLinhasLimpeza }).map((_, index) => {
					const metodoSelecionado = watch(`limpezaMetodo${index}` as any);
					return (
					<Row className="row-custom align-items-end" key={`limpeza-bordadura-${index}`}>
						<Col md={4}>
							<Form.Control ref={register} name={`limpezaNome${index}`} placeholder="Maquinário / equipamento" maxLength={150} />
						</Col>
						<Col md={2}>
							<Controller
								control={control}
								name={`limpezaDataEntrada${index}` as any}
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Col>
						<Col md={2}>
							<Controller
								control={control}
								name={`limpezaDataSaida${index}` as any}
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Col>
						<Col md={2}>
							<MetodoLimpezaSelect
								register={register}
								name={`limpezaMetodo${index}`}
								nameOutros={`limpezaMetodoOutros${index}`}
								metodoSelecionado={metodoSelecionado}
							/>
						</Col>
						<Col md={2}>
							<Form.Control ref={register} name={`limpezaResponsavel${index}`} placeholder="Responsável" maxLength={150} />
						</Col>
						<Col md="auto" className="d-flex justify-content-end">
							{quantidadeLinhasLimpeza > 1 && (
								<Button title="Remover" contained={false} type="button" onClick={() => removerLinhaLimpeza(index)} />
							)}
						</Col>
					</Row>
					);
				})}

				<div className="button-position" style={{ justifyContent: 'flex-start' }}>
					<Button title="Adicionar equipamento" contained={false} type="button" onClick={adicionarLinhaLimpeza} />
				</div>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Observações e informações adicionais</Form.Label>
						<Form.Control
							as="textarea"
							rows={3}
							ref={register}
							name="observacoesDestruicaoBordadura"
							placeholder="Registre observações adicionais sobre a destruição da bordadura"
							maxLength={500}
						/>
						{errors.observacoesDestruicaoBordadura && <span className="error-field">{errors.observacoesDestruicaoBordadura.message}</span>}
					</Form.Group>
				</Form.Row>

				<p className="mt-2">
					<small>Em caso de dúvidas consulte sempre a CIBio BASF – cibio@basf.com.</small>
				</p>

				<div className="button-position">
					<Button title="Cancelar" contained={false} type="button" onClick={onHide} />
					<Button title="Salvar" contained={true} type="submit" />
				</div>
			</Form>
		</Modal>
	);
}

